import { InformationCircleIcon, TruckIcon } from '@heroicons/react/24/outline';
import { useNavigate } from '@tanstack/react-router';
import { subYears } from 'date-fns/subYears';
import { useCallback, useMemo } from 'react';
import { SmallSecondaryButton } from '../../components/buttons';
import { DownloadUploaderButton } from '../../components/download-uploader-button';
import { EmptyState } from '../../components/empty-state';
import { Loading } from '../../components/loading';
import { Tooltip } from '../../components/popover/tooltip';
import { Spinner } from '../../components/spinner';
import { Cell, Row, Table } from '../../components/tables';
import { lastReceivedDate, type Vendor, yearlyPurchases } from '../../data/vendor';
import { compactCurrencyFormatter } from '../../helpers/format-currency';
import { formatDate } from '../../helpers/format-date';

import { useProducts } from '../../hooks/products';
import { useVendorIntegrations, useVendors } from '../../hooks/vendors';
import { storeLayout } from '../../router';

export function Vendors() {
  const { vendors, loading: vendorsLoading } = useVendors();
  const { products, loading: productsLoading } = useProducts();
  const integrations = useVendorIntegrations();
  const integrationByName = useMemo(
    () => new Map(integrations.map((i) => [i.normalized_name, i])),
    [integrations]
  );
  const { store } = storeLayout.useRouteContext();

  const navigate = useNavigate();
  const editVendor = useCallback(
    (vendor: Vendor) => {
      navigate({
        to: '/stores/$storeId/vendors/$vendorId/edit',
        params: { storeId: store.id, vendorId: vendor.vendor_id },
      });
    },
    [navigate, store]
  );

  if (vendorsLoading) {
    return <Loading />;
  }

  return (
    <>
      {vendors.length > 0 && (
        <div className="container mx-auto">
          <Table
            headings={[
              'ID',
              'Name',
              <div key="purchases" className="flex items-center gap-1">
                Last 365d Purchases
                <Tooltip
                  content="Approximate purchase amount from each vendor over the last 365 days. Product costs are attributed to the vendor they were last purchased from."
                  side="bottom"
                >
                  <span>
                    <InformationCircleIcon className="h-5 w-5 text-gray-500 hover:text-gray-700" />
                  </span>
                </Tooltip>
              </div>,
              'Last Received',
              'Account #',
              'Terms',
              'Emails',
              'Features',
              'Enabled',
              '',
            ]}
          >
            {vendors.map((vendor) => (
              <Row key={vendor.vendor_id}>
                <Cell>{vendor.vendor_id}</Cell>
                <Cell>{vendor.name}</Cell>
                <Cell>
                  {productsLoading ? (
                    <Spinner />
                  ) : (
                    compactCurrencyFormatter(yearlyPurchases(vendor, products))
                  )}
                </Cell>
                <Cell>
                  {productsLoading ? (
                    <Spinner />
                  ) : (
                    (() => {
                      const lastReceived = lastReceivedDate(vendor, products);
                      const oneYearAgo = subYears(new Date(), 1);

                      return lastReceived < oneYearAgo ? '-' : formatDate(lastReceived, new Date());
                    })()
                  )}
                </Cell>
                <Cell>{vendor.account_number}</Cell>
                <Cell>{vendor.terms}</Cell>
                <Cell>
                  <ul>
                    {(vendor.order_settings.representatives || []).map((rep, index) => (
                      <li key={index}>{rep.email}</li>
                    ))}
                  </ul>
                </Cell>
                <Cell>
                  {(() => {
                    const integration = integrationByName.get(vendor.normalized_name);
                    if (!integration) {
                      return '';
                    }

                    const features = [];
                    if (integration.receiving) {
                      features.push('Receiving');
                    }

                    if (integration.pricing) {
                      features.push('Deals');
                    }

                    return features.join(', ');
                  })()}
                </Cell>
                <Cell>{vendor.disabled ? '❌' : '✅'}</Cell>
                <Cell>
                  <SmallSecondaryButton onClick={() => editVendor(vendor)}>
                    Edit
                  </SmallSecondaryButton>
                </Cell>
              </Row>
            ))}
          </Table>
        </div>
      )}
      {vendors.length === 0 &&
        (store.last_uploaded_at == null ? (
          <EmptyState
            icon={<TruckIcon className="size-8" />}
            title="You haven't imported any vendors yet."
            description="Install the uploader to start syncing your LiquorPOS vendors."
            action={<DownloadUploaderButton primary />}
            footnote="Vendors appear here once you record purchases in LiquorPOS and the uploader is syncing."
          />
        ) : (
          <EmptyState
            icon={<TruckIcon className="size-8" />}
            title="No vendors yet."
            description="Vendors will appear here once you record purchases in LiquorPOS and your next upload runs."
          />
        ))}
    </>
  );
}
